import { useState, useMemo } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faLinkedin,
  faFacebook,
  faInstagram,
  faXTwitter,
  faYoutube,
  faTiktok,
  faPinterest,
  faThreads,
  faGoogle,
  faBluesky,
} from '@fortawesome/free-brands-svg-icons';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import { Checkbox } from '@/components/ui/Checkbox';
import { cn } from '@/utils/cn';
import { filterTriggerHover } from './filterTriggerStyles';

/**
 * ChannelMultiSelect Component
 *
 * Multi-select dropdown for channels with platform icons
 * Shows FontAwesome brand icons for each platform
 */

interface PlatformInfo {
  color: string;
  icon: IconDefinition;
  name: string;
}

const PLATFORM_INFO: Record<string, PlatformInfo> = {
  LINKEDIN: { color: '#0A66C2', icon: faLinkedin, name: 'LinkedIn' },
  FACEBOOK: { color: '#1877F2', icon: faFacebook, name: 'Facebook' },
  INSTAGRAM: { color: '#E4405F', icon: faInstagram, name: 'Instagram' },
  TWITTER: { color: '#000000', icon: faXTwitter, name: 'X' },
  YOUTUBE: { color: '#FF0000', icon: faYoutube, name: 'YouTube' },
  TIKTOK: { color: '#000000', icon: faTiktok, name: 'TikTok' },
  PINTEREST: { color: '#E60023', icon: faPinterest, name: 'Pinterest' },
  THREADS: { color: '#000000', icon: faThreads, name: 'Threads' },
  GOOGLE_MY_BUSINESS: { color: '#4285F4', icon: faGoogle, name: 'Google' },
  BLUESKY: { color: '#0085FF', icon: faBluesky, name: 'Bluesky' },
  // Note: CANVA, GDRIVE, WORDPRESS are intentionally omitted — they're
  // non-social asset/blog channels (see NON_SOCIAL_CHANNEL_PLATFORMS in
  // '@/constants/platforms') and callers of this component are expected to
  // filter them out of `options` before passing them in.
};

const getPlatformInfo = (platform: string): PlatformInfo => {
  return PLATFORM_INFO[platform] || { color: '#6B7280', icon: faGlobe, name: platform };
};

export interface ChannelOption {
  id: number;
  name: string;
  platform: string;
  avatarUrl?: string | null;
}

export interface ChannelMultiSelectProps {
  value: number[];
  onChange: (ids: number[]) => void;
  options: ChannelOption[];
  placeholder?: string;
  label?: string;
  isLoading?: boolean;
  disabled?: boolean;
  className?: string;
}

export const ChannelMultiSelect = ({
  value,
  onChange,
  options,
  placeholder = 'Select channels...',
  label,
  isLoading = false,
  disabled = false,
  className = '',
}: ChannelMultiSelectProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;

    const searchLower = search.toLowerCase();
    return options.filter(
      (option) =>
        option.name.toLowerCase().includes(searchLower) ||
        option.platform.toLowerCase().includes(searchLower)
    );
  }, [options, search]);

  const toggleOption = (optionId: number) => {
    if (value.includes(optionId)) {
      onChange(value.filter((id) => id !== optionId));
    } else {
      onChange([...value, optionId]);
    }
  };

  const clearAll = () => {
    onChange([]);
  };

  const selectedOptions = options.filter((opt) => value.includes(opt.id));

  const removeOption = (e: React.MouseEvent, optionId: number) => {
    e.stopPropagation();
    onChange(value.filter((id) => id !== optionId));
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearch('');
    }
  };

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      )}

      <PopoverPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverPrimitive.Trigger asChild>
          <button
            type="button"
            disabled={disabled || isLoading}
            className={cn(
              'w-full px-4 py-2 text-sm',
              'border border-gray-200 rounded-lg',
              'text-left',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
              'transition-all duration-200',
              'disabled:bg-gray-50 disabled:cursor-not-allowed',
              'flex items-center justify-between',
              !(disabled || isLoading) && filterTriggerHover
            )}
          >
            <span className="flex-1 min-w-0">
              {isLoading ? (
                <span className="text-gray-400 flex items-center gap-2">
                  <svg
                    className="animate-spin h-4 w-4 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Loading...
                </span>
              ) : selectedOptions.length === 0 ? (
                <span className="text-gray-400">{placeholder}</span>
              ) : (
                <span className="flex flex-wrap gap-1">
                  {selectedOptions.slice(0, 4).map((opt) => {
                    const platformInfo = getPlatformInfo(opt.platform);
                    return (
                      <span
                        key={opt.id}
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-gray-100 rounded text-xs font-medium"
                      >
                        {opt.avatarUrl ? (
                          <img
                            src={opt.avatarUrl}
                            alt=""
                            className="w-4 h-4 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (
                          <span
                            className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: platformInfo.color }}
                          >
                            <FontAwesomeIcon
                              icon={platformInfo.icon}
                              className="text-white text-[8px]"
                            />
                          </span>
                        )}
                        <span className="truncate max-w-[80px] text-gray-700">{opt.name}</span>
                        <button
                          type="button"
                          onClick={(e) => removeOption(e, opt.id)}
                          className="hover:bg-gray-200 rounded-full p-0.5 -mr-1"
                        >
                          <svg
                            className="w-3 h-3 text-gray-500"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </span>
                    );
                  })}
                  {selectedOptions.length > 4 && (
                    <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                      {t('multiselect_overflow', { count: selectedOptions.length - 4 })}
                    </span>
                  )}
                </span>
              )}
            </span>
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={cn('transition-transform duration-200', isOpen && 'rotate-180')}
            >
              <path
                d="M2.5 4.5L6 8L9.5 4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </PopoverPrimitive.Trigger>

        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            className={cn(
              'bg-white rounded-lg shadow-lg',
              'border border-gray-200',
              'w-[var(--radix-popover-trigger-width)]',
              'max-h-[320px]',
              'flex flex-col',
              'z-popover-top'
            )}
            sideOffset={5}
            align="start"
          >
            {/* Search Input */}
            <div className="p-3 border-b border-gray-100">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search channels..."
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                autoFocus
              />
            </div>

            {/* Options List */}
            <div className="overflow-y-auto flex-1 p-2">
              {filteredOptions.length > 0 ? (
                <div className="space-y-1">
                  {filteredOptions.map((option) => {
                    const isSelected = value.includes(option.id);
                    const platformInfo = getPlatformInfo(option.platform);

                    return (
                      <label
                        key={option.id}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2',
                          'hover:bg-gray-50 rounded-md cursor-pointer',
                          'transition-colors'
                        )}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleOption(option.id)}
                        />
                        {/* Avatar with platform icon badge */}
                        <div className="relative flex-shrink-0">
                          {option.avatarUrl ? (
                            <img
                              src={option.avatarUrl}
                              alt=""
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <span
                              className="w-8 h-8 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: platformInfo.color }}
                            >
                              <FontAwesomeIcon
                                icon={platformInfo.icon}
                                className="text-white text-sm"
                              />
                            </span>
                          )}
                          {/* Platform badge (shown when avatar exists) */}
                          {option.avatarUrl && (
                            <span
                              className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center border-2 border-white"
                              style={{ backgroundColor: platformInfo.color }}
                            >
                              <FontAwesomeIcon
                                icon={platformInfo.icon}
                                className="text-white text-[7px]"
                              />
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-gray-700 font-medium truncate">
                            {option.name}
                          </div>
                          <div className="text-xs text-gray-500">{platformInfo.name}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="p-4 text-center">
                  <p className="text-sm text-gray-500">
                    {search ? 'No channels found' : 'No channels available'}
                  </p>
                </div>
              )}
            </div>

            {/* Footer with Clear All */}
            {value.length > 0 && (
              <div className="p-2 border-t border-gray-100 bg-gray-50">
                <button
                  type="button"
                  onClick={clearAll}
                  className="w-full px-3 py-2 text-xs text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors font-medium"
                >
                  {t('clear_all')} ({value.length} {t('selected', { count: value.length })})
                </button>
              </div>
            )}
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    </div>
  );
};
